import { CollectService } from '@/api/generated'
import type {
  CollectAnswersPatchRequest,
  CollectProbeRequest,
  CollectProbeResponse,
  CollectSessionOpenRequest,
  CollectSessionOpenedResponse,
  CollectSessionResponse,
  CollectSignalResponse,
} from '@/api/generated'

type SessionTokenHeaders = { 'X-Session-Token': string }
type LiveTranscriptionAudioHeaders = SessionTokenHeaders & {
  'X-Signal-Question': string
  'X-Signal-Turn'?: number
}
type VoiceInputHeaders = SessionTokenHeaders & { 'X-Signal-Question': string; 'Content-Type': string }

function sessionHeaders(accessToken: string): SessionTokenHeaders {
  return { 'X-Session-Token': accessToken }
}

function liveTranscriptionAudioHeaders(
  accessToken: string,
  questionId: string,
  turn: number
): LiveTranscriptionAudioHeaders {
  return {
    'X-Session-Token': accessToken,
    'X-Signal-Question': questionId,
    'X-Signal-Turn': turn,
  }
}

function voiceInputHeaders(accessToken: string, questionId: string, contentType: string): VoiceInputHeaders {
  return { 'X-Session-Token': accessToken, 'X-Signal-Question': questionId, 'Content-Type': contentType }
}

/**
 * Single entry point for collection API calls.
 * Components and stores must not import CollectService / DummyService directly.
 */
export const collectApi = {
  async getSignalRuntime(linkToken: string): Promise<CollectSignalResponse> {
    const response = await CollectService.collectSignalRuntime({ path: { link_token: linkToken } })

    return response.data
  },

  async openSession(linkToken: string, body: CollectSessionOpenRequest = {}): Promise<CollectSessionOpenedResponse> {
    const response = await CollectService.collectSessionOpen({ path: { link_token: linkToken }, body })

    return response.data
  },

  async getSessionDetail(sessionId: string, accessToken: string): Promise<CollectSessionResponse> {
    const response = await CollectService.collectSessionDetail({
      path: { session_id: sessionId },
      headers: sessionHeaders(accessToken),
    })

    return response.data
  },

  async patchAnswers(
    sessionId: string,
    answers: CollectAnswersPatchRequest['answers'],
    accessToken: string,
    inputModes?: CollectAnswersPatchRequest['input_modes']
  ): Promise<CollectSessionResponse> {
    const response = await CollectService.collectSessionPatchAnswers({
      path: { session_id: sessionId },
      headers: sessionHeaders(accessToken),
      body: {
        answers,
        ...(inputModes && Object.keys(inputModes).length > 0 ? { input_modes: inputModes } : {}),
      },
    })

    return response.data
  },

  /** One probing turn for an open_text question with `config.probing.enabled`. */
  async probeQuestion(
    sessionId: string,
    questionId: string,
    answer: CollectProbeRequest['answer'],
    accessToken: string,
    inputMode?: CollectProbeRequest['input_mode']
  ): Promise<CollectProbeResponse> {
    const response = await CollectService.collectSessionProbeQuestion({
      path: { session_id: sessionId, question_id: questionId },
      headers: sessionHeaders(accessToken),
      body: {
        answer,
        ...(inputMode ? { input_mode: inputMode } : {}),
      },
    })

    return response.data
  },

  async finalize(sessionId: string, accessToken: string): Promise<CollectSessionResponse> {
    const response = await CollectService.collectSessionFinalize({
      path: { session_id: sessionId },
      headers: sessionHeaders(accessToken),
    })

    return response.data
  },

  async sealPartial(sessionId: string, accessToken: string): Promise<CollectSessionResponse> {
    const response = await CollectService.collectSessionSealPartial({
      path: { session_id: sessionId },
      headers: sessionHeaders(accessToken),
    })

    return response.data
  },

  async startLiveTranscription(sessionId: string, accessToken: string) {
    const response = await CollectService.collectLiveTranscriptionStart({
      path: { session_id: sessionId },
      headers: sessionHeaders(accessToken),
    })

    return response.data
  },

  async uploadLiveTranscriptionAudio(
    sessionId: string,
    accessToken: string,
    chunk: Blob,
    questionId: string,
    turn = 0
  ) {
    await CollectService.collectLiveTranscriptionAudio({
      path: { session_id: sessionId },
      headers: liveTranscriptionAudioHeaders(accessToken, questionId, turn),
      body: chunk,
    })
  },

  async closeLiveTranscription(sessionId: string, accessToken: string) {
    await CollectService.collectLiveTranscriptionClose({
      path: { session_id: sessionId },
      headers: sessionHeaders(accessToken),
    })
  },

  async submitVoiceInput(sessionId: string, accessToken: string, recording: Blob, questionId: string) {
    const response = await CollectService.collectVoiceInput({
      path: { session_id: sessionId },
      headers: voiceInputHeaders(accessToken, questionId, recording.type || 'audio/pcm'),
      body: recording,
    })

    return response.data
  },
}
